import { TRAY_CONFIGS } from '@/constants/hardware/trays';
import type { Nullable, StrictNullable } from '@/types/utility';

type TrayIdentifier = string | number;

type TrayHardwareConfiguration = {
  tray?: StrictNullable<TrayIdentifier>;
  supported_trays?: Record<string, TrayIdentifier>;
};

type DeviceConfiguration = TrayHardwareConfiguration & {
  autosampler?: StrictNullable<TrayHardwareConfiguration>;
};

type VialValue = Nullable<string | number>;

export const getTrayConfig = (
  configuration: Nullable<DeviceConfiguration>,
): StrictNullable<string> => {
  const trayHardware = configuration?.autosampler ?? configuration;
  if (!trayHardware?.supported_trays || trayHardware.tray == null) {
    return null;
  }

  return (
    Object.entries(trayHardware.supported_trays).find(([, id]) => id === trayHardware.tray)?.[0] ??
    null
  );
};

export const getVialLabel = (vial: VialValue, trayConfig?: StrictNullable<string>): VialValue => {
  if (trayConfig !== TRAY_CONFIGS.TRAY_54_6X9) {
    return vial;
  }

  const match = String(vial ?? '').match(/^([A-F])([1-9])$/i);
  if (!match) {
    return vial;
  }

  const row = match[1].toUpperCase().charCodeAt(0) - 'A'.charCodeAt(0);
  return String(row * 9 + Number(match[2]));
};

export const getVialId = (vial: VialValue, trayConfig?: StrictNullable<string>): VialValue => {
  if (trayConfig !== TRAY_CONFIGS.TRAY_54_6X9 || !/^\d{1,2}$/.test(String(vial ?? ''))) {
    return vial;
  }

  const number = Number(vial);
  if (number < 1 || number > 54) {
    return vial;
  }

  const row = String.fromCharCode('A'.charCodeAt(0) + Math.floor((number - 1) / 9));
  const column = ((number - 1) % 9) + 1;
  return `${row}${column}`;
};
